import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import NewProject from "@/pages/NewProject";
import ProjectDetail from "@/pages/ProjectDetail";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { Analytics } from "@vercel/analytics/react";
import "@/App.css";

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for real-time authentication state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F2EB] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#111111]">
        Authenticating...
      </div>
    );
  }

  // Guard for protected pages
  const RequireAuth = ({ children }) => {
    if (!session) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Guard to prevent auth page access when logged in
  const RequireNoAuth = ({ children }) => {
    if (session) {
      return <Navigate to="/app" replace />;
    }
    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Landing View */}
          <Route path="/" element={<Landing session={session} />} />

          {/* Registration and Login */}
          <Route path="/login" element={<RequireNoAuth><Login /></RequireNoAuth>} />
          <Route path="/register" element={<RequireNoAuth><Register /></RequireNoAuth>} />

          {/* Secure App Dashboard */}
          <Route path="/app" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/app/new" element={<RequireAuth><NewProject /></RequireAuth>} />
          <Route path="/app/projects/:id" element={<RequireAuth><ProjectDetail /></RequireAuth>} />
          <Route path="/app/settings" element={<RequireAuth><Settings /></RequireAuth>} />

          {/* Wildcard Fallback redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{
        style: { borderRadius: "12px", border: "1px solid #E8E8ED", fontFamily: "Geist" }
      }} />
      <Analytics />
    </div>
  );
}

export default App;
